import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { formatPickupWindow } from '../utils/dateTimeUtils';
import { getVatBreakdown } from '../utils/vatUtils';
import { useTranslation } from '../hooks/useTranslation';
import './CartPage.css';

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem, getCartTotal } = useCart();
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleQuantityChange = (productId: string, pickupDateId: string, value: string, maxQuantity: number) => {
    const qty = parseFloat(value);
    
    // Clear error for this item
    const errorKey = `${productId}-${pickupDateId}`;
    const newErrors = { ...errors };
    delete newErrors[errorKey];

    if (value === '' || isNaN(qty)) {
      setErrors(newErrors);
      return;
    }

    if (qty <= 0) {
      setErrors({ ...newErrors, [errorKey]: t('cart.quantityPositive') });
      return;
    }

    if (qty > maxQuantity) {
      setErrors({ ...newErrors, [errorKey]: t('cart.quantityExceeds').replace('{max}', maxQuantity.toString()) });
      return;
    }

    setErrors(newErrors);
    updateQuantity(productId, pickupDateId, qty);
  };

  const handleRemoveItem = (productId: string, pickupDateId: string) => {
    removeItem(productId, pickupDateId);
    const errorKey = `${productId}-${pickupDateId}`;
    const newErrors = { ...errors };
    delete newErrors[errorKey];
    setErrors(newErrors);
  };

  const handleProceedToCheckout = () => {
    if (cart.length === 0) {
      alert(t('cart.cartEmpty'));
      return;
    }

    if (Object.keys(errors).length > 0) {
      alert(t('cart.fixErrors'));
      return;
    }

    navigate('/checkout');
  };

  // Group cart items by pickup date
  const groupedCart = cart.reduce((acc, item) => {
    const key = item.pickup_date_id;
    if (!acc[key]) {
      acc[key] = {
        pickup_date_id: item.pickup_date_id,
        pickup_date: item.pickup_date,
        pickup_start_time: item.pickup_start_time,
        pickup_end_time: item.pickup_end_time,
        location_name: item.location_name,
        location_address: item.location_address,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {} as Record<string, any>);

  if (cart.length === 0) {
    return (
      <div className="cart-container" style={{ textAlign: 'center' }}>
        <h1>{t('cart.title')}</h1>
        <p style={{ fontSize: '18px', color: '#666', margin: '40px 0' }}>
          {t('cart.empty')}
        </p>
        <button
          onClick={() => navigate('/products')}
          style={{
            padding: '12px 30px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: 'pointer',
          }}
        >
          {t('cart.browseProducts')}
        </button>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <h1>{t('cart.title')}</h1>

      <div className="cart-grid">
        {/* Cart Items */}
        <section className="cart-items-section">
          {Object.values(groupedCart).map((group: any) => (
            <div key={group.pickup_date_id} style={{ marginBottom: '30px' }}>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px 8px 0 0',
                borderBottom: '2px solid #ddd'
              }}>
                <h3 style={{ margin: '0 0 5px 0' }}>
                  {t('cart.pickup')}: {formatPickupWindow(group.pickup_date, group.pickup_start_time, group.pickup_end_time, t('common.from'), t('common.to'))}
                </h3>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                  <strong>{t('cart.location')}:</strong> {group.location_name}
                </p>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                  {group.location_address}
                </p>
              </div>

              {group.items.map((item: any) => {
                const errorKey = `${item.product_id}-${item.pickup_date_id}`;
                return (
                  <div
                    key={`${item.product_id}-${item.pickup_date_id}`}
                    style={{
                      padding: '20px',
                      border: '1px solid #ddd',
                      borderTop: 'none',
                      backgroundColor: 'white',
                    }}
                  >
                    <div className="cart-item-content">
                      <div className="cart-item-details">
                        <h4 style={{ margin: '0 0 10px 0' }}>{item.product_name}</h4>
                        <p style={{ margin: '5px 0', color: '#666' }}>
                          €{item.price_per_kg.toFixed(2)} {t('common.perKg')} <span style={{ fontSize: '12px' }}>({t('common.inclVat')})</span>
                        </p>
                        
                        <div className="quantity-controls">
                          <label style={{ fontWeight: 'bold' }}>{t('cart.quantityKg')}:</label>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.quantity_kg}
                            onChange={(e) => handleQuantityChange(
                              item.product_id, 
                              item.pickup_date_id, 
                              e.target.value,
                              100 // We don't have max quantity in cart, using a large number
                            )}
                            className={`quantity-input ${errors[errorKey] ? 'error' : ''}`}
                          />
                          <button
                            onClick={() => handleRemoveItem(item.product_id, item.pickup_date_id)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                          >
                            {t('cart.remove')}
                          </button>
                        </div>
                        
                        {errors[errorKey] && (
                          <p style={{ color: '#dc3545', margin: '5px 0 0 0', fontSize: '14px' }}>
                            {errors[errorKey]}
                          </p>
                        )}
                      </div>

                      <div className="cart-item-price">
                        <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                          €{(item.quantity_kg * item.price_per_kg).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <button
            onClick={() => navigate('/products')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            {t('cart.continueShopping')}
          </button>
        </section>

        {/* Order Summary */}
        <section className="cart-summary-section">
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            position: 'sticky',
            top: '20px'
          }}>
            <h2 style={{ marginTop: 0 }}>{t('cart.orderSummary')}</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span>{t('cart.items')}:</span>
                <span>{cart.length}</span>
              </div>
              
              {cart.map(item => (
                <div 
                  key={`${item.product_id}-${item.pickup_date_id}`}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  <span>{item.product_name} ({item.quantity_kg} kg)</span>
                  <span>€{(item.quantity_kg * item.price_per_kg).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div style={{ 
              borderTop: '1px solid #ddd', 
              paddingTop: '15px', 
              marginBottom: '15px' 
            }}>
              {(() => {
                const total = getCartTotal();
                const vatBreakdown = getVatBreakdown(total);
                return (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <span>{t('cart.subtotalExclVat')}:</span>
                      <span>€{vatBreakdown.priceExclVat.toFixed(2)}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <span>{t('cart.vat')}:</span>
                      <span>€{vatBreakdown.vatAmount.toFixed(2)}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '20px', 
                      fontWeight: 'bold',
                      paddingTop: '12px',
                      borderTop: '2px solid #333'
                    }}>
                      <span>{t('cart.totalInclVat')}:</span>
                      <span>€{total.toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <button
              onClick={handleProceedToCheckout}
              disabled={Object.keys(errors).length > 0}
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: Object.keys(errors).length > 0 ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: Object.keys(errors).length > 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {t('cart.proceedToCheckout')}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CartPage;
